<script setup lang="ts">
import { BadgeComponent, copyTextToClipboard, MetapicIcon, type MyPageLink, PlatformIconComponent } from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import { computed, ref, watch } from 'vue'

import { useGetCreatorSegments, useGetUserTagListRequest, useSaveCreatorTags } from '@/api'
import CopyIcon from '@/assets/icons/copy.svg?component'
import EditIcon from '@/assets/icons/edit.svg?component'
import { EditSegmentsModal } from '@/components/CreatorDetail'
import type { CreatorDetail } from '@/lib/types/creator-detail.types'
import { BoxComponent, ButtonComponent, FlexComponent, SelectComponent, TooltipComponent, TypographyComponent } from '@/ui-library'

const MAX_VISIBLE_SEGMENTS = 4

const props = defineProps<{
  creator: CreatorDetail
  myPage?: MyPageLink | null
}>()

const emits = defineEmits<{
  editUsername: []
  editEmail: []
  editPhone: []
  editClient: []
  editTier: []
  editSocials: []
  refresh: []
}>()

const tagSearch = ref('')
const { data: tagSuggestions } = useGetUserTagListRequest(tagSearch)
const { saveTags, isLoading: isSavingTags } = useSaveCreatorTags()
const selectedTagNames = ref<string[]>([])

const creatorIdRef = computed(() => props.creator.id)
const { staticSegments, dynamicSegments, refetch: refetchSegments } = useGetCreatorSegments(creatorIdRef)
const isSegmentsModalOpen = ref(false)

const allSegments = computed(() => [...staticSegments.value, ...dynamicSegments.value])
const visibleSegments = computed(() => allSegments.value.slice(0, MAX_VISIBLE_SEGMENTS))
const hiddenSegmentsCount = computed(() => Math.max(0, allSegments.value.length - MAX_VISIBLE_SEGMENTS))

const tagOptions = computed(() => {
  const current = props.creator.tags.map((t) => ({ label: t.name, value: t.name }))
  const suggestions = tagSuggestions.value
    .filter((t) => !props.creator.tags.some((ct) => ct.name === t.name))
    .map((t) => ({ label: t.name, value: t.name }))
  return [...current, ...suggestions]
})

const haveTagsChanged = computed(() => {
  const original = props.creator.tags.map((t) => t.name).sort()
  const current = [...selectedTagNames.value].sort()
  return original.length !== current.length || original.some((v, i) => v !== current[i])
})

watch(
  () => props.creator.tags,
  (tags) => {
    selectedTagNames.value = tags.map((t) => t.name)
  },
  { immediate: true },
)

const onTagSearchRemote = (query: string) => {
  tagSearch.value = query
}

const onSaveTags = async () => {
  const success = await saveTags(props.creator.id, selectedTagNames.value)
  if (success) {
    emits('refresh')
  }
}
</script>
<template>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">ID</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.id }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>

  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Username</TypographyComponent>
      <FlexComponent align="center" :gap="8">
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.username }}</TypographyComponent>
        <TooltipComponent trigger="click" placement="top" :auto-close="600">
          <template #default>
            <ElIcon :size="16" color="var(--primary)" class="info-action-icon" @click="copyTextToClipboard(creator.username)">
              <CopyIcon />
            </ElIcon>
          </template>
          <template #content>
            <TypographyComponent variant="label-large">Copied!</TypographyComponent>
          </template>
        </TooltipComponent>
      </FlexComponent>
    </BoxComponent>
    <ButtonComponent variant="transparent" is-icon size="small" @click="emits('editUsername')">
      <ElIcon :size="20" color="var(--primary)">
        <EditIcon />
      </ElIcon>
    </ButtonComponent>
  </FlexComponent>

  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Email</TypographyComponent>
      <FlexComponent align="center" :gap="8">
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.email }}</TypographyComponent>
        <TooltipComponent trigger="click" placement="top" :auto-close="600">
          <template #default>
            <ElIcon :size="16" color="var(--primary)" class="info-action-icon" @click="copyTextToClipboard(creator.email)">
              <CopyIcon />
            </ElIcon>
          </template>
          <template #content>
            <TypographyComponent variant="label-large">Copied!</TypographyComponent>
          </template>
        </TooltipComponent>
      </FlexComponent>
    </BoxComponent>
    <ButtonComponent variant="transparent" is-icon size="small" @click="emits('editEmail')">
      <ElIcon :size="20" color="var(--primary)">
        <EditIcon />
      </ElIcon>
    </ButtonComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">First name</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.firstName || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>

  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Surname</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.lastName || '-' }}</TypographyComponent>
    </BoxComponent>
  </FlexComponent>

  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Phone number</TypographyComponent>
      <FlexComponent align="center" :gap="8">
        <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.phone || '-' }}</TypographyComponent>
        <template v-if="creator.phone">
          <TooltipComponent trigger="click" placement="top" :auto-close="600">
            <template #default>
              <ElIcon :size="16" color="var(--primary)" class="info-action-icon" @click="copyTextToClipboard(creator.phone)">
                <CopyIcon />
              </ElIcon>
            </template>
            <template #content>
              <TypographyComponent variant="label-large">Copied!</TypographyComponent>
            </template>
          </TooltipComponent>
        </template>
      </FlexComponent>
    </BoxComponent>
    <ButtonComponent variant="transparent" is-icon size="small" @click="emits('editPhone')">
      <ElIcon :size="20" color="var(--primary)">
        <EditIcon />
      </ElIcon>
    </ButtonComponent>
  </FlexComponent>
  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Client</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.client.name }}</TypographyComponent>
    </BoxComponent>
    <ButtonComponent variant="transparent" is-icon size="small" @click="emits('editClient')">
      <ElIcon :size="20" color="var(--primary)">
        <EditIcon />
      </ElIcon>
    </ButtonComponent>
  </FlexComponent>

  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Revenue tier</TypographyComponent>
      <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ creator.revenueTier.name }}</TypographyComponent>
    </BoxComponent>
    <ButtonComponent variant="transparent" is-icon size="small" @click="emits('editTier')">
      <ElIcon :size="20" color="var(--primary)">
        <EditIcon />
      </ElIcon>
    </ButtonComponent>
  </FlexComponent>

  <FlexComponent align="center" class="info-field">
    <BoxComponent class="info-field__content">
      <TypographyComponent variant="label-large">Segments</TypographyComponent>
      <BoxComponent :mt="8">
        <FlexComponent v-if="allSegments.length" :gap="8" align="center" wrap="wrap">
          <span v-for="segment in visibleSegments" :key="segment.id" class="segment-chip">{{ segment.name }}</span>
          <span v-if="hiddenSegmentsCount" class="segment-chip">+{{ hiddenSegmentsCount }}</span>
        </FlexComponent>
        <TypographyComponent v-else variant="body-medium" color="var(--secondary-48)">Not assigned to segments</TypographyComponent>
      </BoxComponent>
    </BoxComponent>
    <ButtonComponent variant="transparent" is-icon size="small" data-testid="edit-segments-button" @click="isSegmentsModalOpen = true">
      <ElIcon :size="20" color="var(--primary)">
        <EditIcon />
      </ElIcon>
    </ButtonComponent>
  </FlexComponent>

  <BoxComponent class="info-field">
    <TypographyComponent variant="label-large">Tags</TypographyComponent>
    <BoxComponent :mt="8">
      <FlexComponent :gap="8" align="center">
        <SelectComponent
          v-model="selectedTagNames"
          :options="tagOptions"
          placeholder="Select or search tags"
          multiple
          filterable
          remote
          allow-create
          default-first-option
          :reserve-keyword="false"
          :remote-method="onTagSearchRemote"
          class="info-tags-select"
        />
        <ButtonComponent
          variant="primary"
          :is-loading="isSavingTags"
          :style="{ visibility: haveTagsChanged ? 'visible' : 'hidden' }"
          @click="onSaveTags"
        >
          Save
        </ButtonComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>

  <BoxComponent class="info-field info-socials">
    <FlexComponent align="center" :gap="16" class="info-social-header">
      <TypographyComponent variant="label-large" :font-weight="600" class="info-field__content">Social media profiles</TypographyComponent>
      <ButtonComponent
        variant="transparent"
        is-icon
        size="big"
        aria-label="Edit social media profiles"
        class="info-social-edit"
        data-testid="edit-socials-button"
        @click="emits('editSocials')"
      >
        <ElIcon :size="24" color="var(--primary)">
          <EditIcon />
        </ElIcon>
      </ButtonComponent>
    </FlexComponent>
    <BoxComponent :mt="8">
      <FlexComponent direction="column" :gap="8">
        <FlexComponent v-for="social in creator.socials" :key="social.id" align="center" :gap="8" class="info-social-row">
          <a :href="social.link" target="_blank" rel="noopener noreferrer" class="info-social-link">
            <span class="info-social-platform">
              <PlatformIconComponent :platform="social.type" :size="24" :is-connected="social.isConnected" style="color: var(--primary)" />
              <TypographyComponent variant="body-medium">{{ social.title }}</TypographyComponent>
            </span>
            <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ social.identifier }}</TypographyComponent>
          </a>
          <TooltipComponent trigger="click" placement="top" :auto-close="600" class="info-copy-tooltip">
            <template #default>
              <ElIcon
                :size="18"
                color="var(--primary)"
                class="info-action-icon"
                @click.stop.prevent="copyTextToClipboard(social.identifier)"
              >
                <CopyIcon />
              </ElIcon>
            </template>
            <template #content>
              <TypographyComponent variant="label-large">Copied!</TypographyComponent>
            </template>
          </TooltipComponent>
          <BadgeComponent v-if="social.isPrimary" variant="accent" size="small">Main</BadgeComponent>
        </FlexComponent>
        <FlexComponent v-if="props.myPage" align="center" :gap="8" class="info-my-page">
          <a :href="props.myPage.url" target="_blank" rel="noopener noreferrer" class="info-social-link">
            <span class="info-social-platform">
              <ElIcon :size="24" color="var(--primary)" class="info-my-page-icon">
                <MetapicIcon />
              </ElIcon>
              <TypographyComponent variant="body-medium">My page</TypographyComponent>
            </span>
            <TypographyComponent variant="body-medium" color="var(--secondary-48)">{{ props.myPage.slug }}</TypographyComponent>
          </a>
          <TooltipComponent trigger="click" placement="top" :auto-close="600" class="info-copy-tooltip">
            <template #default>
              <ElIcon
                :size="18"
                color="var(--primary)"
                class="info-action-icon"
                @click.stop.prevent="copyTextToClipboard(props.myPage.slug)"
              >
                <CopyIcon />
              </ElIcon>
            </template>
            <template #content>
              <TypographyComponent variant="label-large">Copied!</TypographyComponent>
            </template>
          </TooltipComponent>
        </FlexComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
  <EditSegmentsModal
    v-if="isSegmentsModalOpen"
    v-model="isSegmentsModalOpen"
    :creator-id="creator.id"
    :static-segments="staticSegments"
    :dynamic-segments="dynamicSegments"
    @changed="refetchSegments"
  />
</template>
<style scoped lang="scss">
.info-field {
  padding: 16px 32px;
  border-bottom: 1px solid var(--secondary-4);
  align-items: center;

  &:last-child {
    border-bottom: none;
  }
}

.info-field__content {
  flex: 1;
  min-width: 0;
}

.info-action-icon {
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
}

.segment-chip {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: rgba(34, 34, 34, 0.12);
  color: var(--primary);
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info-field.info-socials {
  --primary: #222222;

  padding: 8px 4px 8px 16px;
  border-radius: 16px;
}

.info-social-header {
  background: var(--inverted);
  border-radius: 16px;
}

.info-copy-tooltip {
  display: flex;
  flex-shrink: 0;
}

.info-social-edit {
  width: 48px;
  flex-shrink: 0;
}

.info-socials :deep(.badge--accent) {
  box-shadow: none;
}

.info-social-link {
  display: flex;
  align-items: center;
  gap: 8px;
  color: inherit;
  text-decoration: none;
  min-width: 0;

  &:hover {
    text-decoration: underline;
  }
}

.info-social-platform {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
  width: 120px;
}

.info-my-page-icon {
  flex-shrink: 0;

  :deep(svg) {
    width: 100%;
    height: 100%;
  }
}

.info-tags-select {
  flex: 1;
  min-width: 0;
}

.info-tags-select :deep(.el-select__wrapper) {
  height: auto;
  min-height: 48px;
  padding: 14px 24px;
}

.info-tags-select :deep(.el-select__selection) {
  gap: 8px;
}

.info-tags-select :deep(.el-select__selection .el-tag.el-tag) {
  background-color: rgba(34, 34, 34, 0.12);
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  height: auto;
  margin: 0;
  color: var(--primary);
  font-weight: 600;
  font-size: 14px;
  line-height: 20px;
}

.info-tags-select :deep(.el-select__selection .el-tag.el-tag .el-tag__close) {
  width: 18px;
  height: 18px;
  font-size: 14px;
  color: var(--primary);

  &:hover {
    background: transparent;
    color: var(--primary);
  }
}

:deep(.el-icon path) {
  fill: currentColor;
}
</style>
